
import { useNavigate, useParams } from "react-router";
import { Button, Form, Spinner } from "react-bootstrap";
import { Heading } from "../component/Heading";
import { useEffect, useState, type FormEvent } from "react";
import * as yup from "yup";
import { useCartContext } from "../context/cartContext";
import server from "../server";
import _ from "lodash";
import axios from "axios";


type ApiErrorData = {
  error?: { message?: string };
  message?: string;
};
// ─── Yup Schema ──────────────────────────────────────────────────────────────

const schema = yup.object({
  email: yup
    .string()
    .required("請輸入電郵地址")
    .email("請輸入有效的電郵地址"),
  phone_number: yup
    .string()
    .required("請輸入電話號碼")
    .matches(/^[0-9]{8}$/, "請輸入有效的香港電話號碼（8位數字）"),
  name: yup.string().required("請輸入姓名"),
});

type FormValues = yup.InferType<typeof schema>;
type FieldErrors = Partial<Record<keyof FormValues, string>>;

// ─── Toast ───────────────────────────────────────────────────────────────────

type ToastState = { message: string; type: "success" | "error" } | null;

function ToastMessage({ toast, onClose }: { toast: ToastState; onClose: () => void }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [toast, onClose]);

  if (!toast) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 100,
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: toast.type === "success" ? "#22c55e" : "#ef4444",
        color: "white",
        padding: "12px 24px",
        borderRadius: 8,
        zIndex: 9999,
        fontSize: 14,
        fontWeight: "600",
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        whiteSpace: "nowrap",
      }}
    >
      {toast.message}
    </div>
  );
}

// ─── Checkout ─────────────────────────────────────────────────────────────────

export default function Checkout() {
  const navigate = useNavigate();
  const { company: companyParam } = useParams();
  const [toast, setToast] = useState<ToastState>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const { cartCounting, sendCartToServer, saveCartToStorage } = useCartContext();
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (cartCounting === 0) {
      navigate(companyParam ? `/${companyParam}` : "/");
    }
  }, [cartCounting, navigate, companyParam]);

  if (cartCounting === 0) return null;
  const showToast = (message: string, type: "success" | "error") =>
    setToast({ message, type });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    setSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const values: FormValues = {
      email: String(formData.get("email") ?? ""),
      phone_number: String(formData.get("phone_number") ?? ""),
      name: String(formData.get("name") ?? ""),
    };

    try {
      await schema.validate(values, { abortEarly: false });
      setErrors({});
      console.log(values);
      const user = {
        email: values.email,
        phone: values.phone_number,
        ...(!_.isEmpty(values.name) && { name: values.name }),
      }
      const session_id = await sendCartToServer(user.email, user.phone);
      console.log({ session_id });
      if (!session_id) {
        throw new Error("Failed to create transaction session");
      }
      const result = await server.post(`user-transaction/generate-campaign`, {
        cart_types: ["campaign_cart"],
        campaign_user: user,
        session_id

      })
      if (companyParam) {
        sessionStorage.setItem("company", companyParam)
      }
      console.log(result?.paymentUrl);
      if (result?.paymentUrl) {
        // navigate(result.paymentUrl);
        saveCartToStorage();
        window.location.assign(result.paymentUrl);
      }

      // navigate(`/payment/success`);

    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const fieldErrors: FieldErrors = {};
        err.inner.forEach((e) => {
          if (e.path) fieldErrors[e.path as keyof FormValues] = e.message;
        });
        setErrors(fieldErrors);
        showToast(err.inner[0]?.message ?? "請檢查表單", "error");
      } else {
        const message = axios.isAxiosError(err)
          ? ((err.response?.data as ApiErrorData | undefined)?.error?.message ??
            (err.response?.data as ApiErrorData | undefined)?.message ??
            err.message)
          : err instanceof Error
            ? err.message
            : "提交失敗，請稍後再試";

        showToast(message, "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main style={{ minHeight: "100vh" }}>
      <ToastMessage toast={toast} onClose={() => setToast(null)} />

      <Form
        className="col-12 col-sm-6 col-lg-4 mx-auto mt-5 px-4"
        onSubmit={handleSubmit}
        noValidate
      >
        {/* Email */}
        <Form.Group className="mb-3" controlId="email">
          <Form.Label>電郵地址 （必填）</Form.Label>
          <Form.Control
            name="email"
            type="email"
            className="bg-white"
            placeholder="輸入電郵地址"
            isInvalid={submitted && !!errors.email}
            isValid={submitted && !errors.email}
          />
          <Form.Control.Feedback type="invalid">
            {errors.email}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Phone */}
        <Form.Group className="mb-3" controlId="phone_number">
          <Form.Label>電話號碼 （必填）</Form.Label>
          <Form.Control
            name="phone_number"
            type="tel"
            className="bg-white"
            placeholder="輸入電話號碼"
            isInvalid={submitted && !!errors.phone_number}
            isValid={submitted && !errors.phone_number}
          />
          <Form.Control.Feedback type="invalid">
            {errors.phone_number}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Name */}
        <Form.Group className="mb-3" controlId="name">
          <Form.Label>姓名 （必填）</Form.Label>
          <Form.Control name="name" type="text" placeholder="輸入姓名" className="bg-white" />
        </Form.Group>

        <Button disabled={submitting} variant="primary" type="submit">
          {submitting ?
            <Spinner animation="border" role="status" size="sm">
              <span className="visually-hidden">Loading...</span>
            </Spinner> : <span>提交</span>
          }
        </Button>
      </Form>
    </main>
  );
}